/**
 * Cart Store
 * Alpine.js store para el estado global del carrito
 *
 * Miercoles de Maki: Shopify BXGY handles the discount.
 * We just show a hint to encourage users to add pairs.
 */

// Detectar maki por nombre del producto (contiene "maki")
const isMakiProduct = (item) => {
  const title = (item.product_title || '').toLowerCase();
  return title.includes('maki');
};

export default function cartStore() {
  return {
    isOpen: false,
    itemCount: 0,
    cartData: null,
    isUpdating: false,
    justAdded: false,
    upsellDismissed: false,

    init() {
      // Restore upsell dismissed state from session
      this.upsellDismissed = sessionStorage.getItem('bebidas_upsell_dismissed') === 'true';
    },

    dismissUpsell() {
      this.upsellDismissed = true;
      sessionStorage.setItem('bebidas_upsell_dismissed', 'true');
    },

    get hasBebidas() {
      if (!this.cartData || !this.cartData.items) return false;
      return this.cartData.items.some(item => item.product_type === 'Bebidas');
    },

    get shouldShowBebidaUpsell() {
      return this.itemCount > 0 && !this.hasBebidas && !this.upsellDismissed;
    },

    // Detectar si es miercoles (dia de promo)
    get isWednesday() {
      const day = new Date().getDay();
      return day === 3; // 3=miercoles
    },

    // Detectar si hay maki en carrito
    get hasMakiInCart() {
      if (!this.cartData || !this.cartData.items) return false;
      return this.cartData.items.some(item => isMakiProduct(item));
    },

    // Verificar si un item es maki
    isMaki(item) {
      return isMakiProduct(item);
    },

    // Verificar si un item tiene descuento BXGY aplicado
    hasDiscount(item) {
      if (!item) return false;
      return item.discount_allocations && item.discount_allocations.length > 0;
    },

    // Verificar si un item es el regalo gratis (precio final = 0)
    // Simplificado: cualquier item con precio 0 es un regalo
    isFreeGift(item) {
      if (!item) return false;
      // Check for 0 price (handles both number 0 and string "0")
      return item.final_line_price === 0 || item.final_line_price === '0';
    },

    // Obtener el total de descuento para un item
    getDiscountAmount(item) {
      if (!item || !item.discount_allocations) return 0;
      return item.discount_allocations.reduce((sum, alloc) => sum + alloc.amount, 0);
    },

    // Verificar si un maki es elegible para promo 2x1 (miercoles + maki)
    isPromoEligible(item) {
      return this.isWednesday && isMakiProduct(item);
    },

    // Hint para animar al usuario a completar el 2x1
    get makiPromoHint() {
      if (!this.isWednesday) return null;
      if (!this.cartData || !this.cartData.items) return null;

      // Contar TODOS los makis (pagados + regalos)
      const totalMakis = this.cartData.items
        .filter(item => isMakiProduct(item))
        .reduce((sum, item) => sum + item.quantity, 0);

      // Si el total de makis es impar, mostrar hint para completar el par
      if (totalMakis > 0 && totalMakis % 2 !== 0) {
        return {
          message: 'Anade uno mas y llevate el 2x1'
        };
      }

      return null;
    },

    async open() {
      await this.fetchCart();
      this.isOpen = true;
      document.body.style.overflow = 'hidden';
    },

    close() {
      this.isOpen = false;
      document.body.style.overflow = '';
    },

    toggle() {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    },

    /**
     * Fetch cart data from Shopify
     * NO ejecuta promo logic - eso solo se hace en addItem()
     */
    async fetchCart() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        this.cartData = cart;
        this.itemCount = cart.item_count;
        document.dispatchEvent(new CustomEvent('cart:updated'));
        return cart;
      } catch (error) {
        console.error('[Cart] Error fetching cart:', error);
        return null;
      }
    },

    async updateCount() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        this.itemCount = cart.item_count;
      } catch (error) {
        console.error('[Cart] Error updating cart count:', error);
      }
    },

    async updateItem(line, quantity) {
      this.isUpdating = true;
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line, quantity })
        });

        if (!response.ok) throw new Error('Failed to update cart');

        const cart = await response.json();
        this.cartData = cart;
        this.itemCount = cart.item_count;
        document.dispatchEvent(new CustomEvent('cart:updated'));

        return cart;
      } catch (error) {
        console.error('[Cart] Error updating cart item:', error);
        return null;
      } finally {
        this.isUpdating = false;
      }
    },

    async removeItem(line) {
      return this.updateItem(line, 0);
    },

    async addItem(variantId, quantity = 1) {
      this.isUpdating = true;
      try {
        // Si ya existe en carrito, usar updateItem en lugar de crear nueva linea
        const existingLine = this.getItemLine(variantId);
        if (existingLine) {
          const currentQty = this.getItemQuantity(variantId);
          this.isUpdating = false; // updateItem maneja su propio isUpdating
          return this.updateItem(existingLine, currentQty + quantity);
        }

        // Nuevo item - añadir al carrito
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: variantId,
            quantity: quantity
          })
        });

        if (!response.ok) throw new Error('Failed to add to cart');

        // Fetch cart to update state
        await this.fetchCart();

        this.itemAdded();
        return this.cartData;
      } catch (error) {
        console.error('[Cart] Error adding to cart:', error);
        return null;
      } finally {
        this.isUpdating = false;
      }
    },

    getItemQuantity(variantId) {
      if (!this.cartData || !this.cartData.items) return 0;
      const item = this.cartData.items.find(item => item.variant_id === variantId);
      return item ? item.quantity : 0;
    },

    getItemLine(variantId) {
      if (!this.cartData || !this.cartData.items) return null;
      const index = this.cartData.items.findIndex(item => item.variant_id === variantId);
      return index >= 0 ? index + 1 : null;
    },

    formatMoney(cents) {
      return (cents / 100).toFixed(2).replace('.', ',') + ' €';
    },

    itemAdded() {
      this.justAdded = true;
      setTimeout(() => {
        this.justAdded = false;
      }, 600);
    }
  };
}
